
































































































































































































































































































































































































import {
  defineComponent,
  ref,
  computed,
  useFetch,
  useRouter
} from '~/utils/nuxt3-migration'
import CButton from '~/components/shared/configurable/button/CButton.vue'
import CImg from '~/components/shared/configurable/image/CImg.vue'
import CCard from '~/components/shared/configurable/card/CCard.vue'
import CConfirmationModal from '~/components/shared/configurable/modal/CConfirmationModal.vue'
import CLink from '~/components/shared/configurable/link/CLink.vue'
import CPagination from '~/components/shared/configurable/pagination/CPagination.vue'
import {
  faExclamationTriangle,
  faTimes
} from '@fortawesome/free-solid-svg-icons'
import { useDep } from '~/compositions/dependency-container'
import SaleRequestsService from '~/services/sale-requests/SaleRequestsService'
import { AxiosError } from 'axios'
import { useSnackbar } from '~/compositions/snackbar'
import { useLogger } from '~/compositions/logger'
import { useI18n } from '~/compositions/i18n'
import { handleError } from '~/utils/http'
import { GuestSaleRequest, ManagerSaleRequest } from '~/models/sale-requests'
import { Pagination } from '~/models/search/types'
import { formatNumber } from '~/utils/number'
import { format } from '~/utils/date'
import { SIMPLE_DATE_FORMAT } from '~/constants/date'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import { useNamespacedStore } from '~/compositions/store'
import ScrollService from '~/services/scroll/ScrollService'
import CCollapse from '~/components/shared/configurable/collapse/CCollapse.vue'
import PlusMinusCollapseOpener from '~/components/shared/configurable/collapse/PlusMinusCollapseOpener.vue'
import SaleRequestStatusBadge from '~/components/car/sale-requests/SaleRequestStatusBadge.vue'
import { ciSaleRequest } from '~/icons/source/solid/sale-request'
import { defineComponentTranslations } from '~/utils/i18n'
import { ciInfoCircle } from '~/icons/source/regular/info-circle'
import { AccessToggleName } from '~/models/user/access-toggles'

export default defineComponent({
  components: {
    SaleRequestStatusBadge,
    CButton,
    CImg,
    CCard,
    CConfirmationModal,
    CLink,
    CPagination,
    CCollapse,
    PlusMinusCollapseOpener
  },
  setup() {
    const scrollService = useDep(ScrollService)
    const saleRequestsService = useDep(SaleRequestsService)
    const snackbar = useSnackbar()
    const logger = useLogger()
    const { t } = useI18n()
    const { getters: userGetters } = useNamespacedStore<UserState>(USER_NS)
    const router = useRouter()

    const loading = ref(false)
    const showCancellationModal = ref(false)
    const showBuyConfirmationModal = ref(false)
    const showInfoModal = ref(false)
    const tableRows = ref<ManagerSaleRequest[] | GuestSaleRequest[]>()
    const pagination = ref<Pagination>()
    const requestIdToCancel = ref()
    const actionStatus = ref('')
    const currentPage = ref()

    const userIsBuyer = computed(() =>
      userGetters('userHasAccessTo')(AccessToggleName.SALE_REQUESTS_BUY)
    )

    const emptyStateTitle = computed(() => {
      return userIsBuyer.value
        ? t('no_buy_requests_found')
        : t('no_sale_requests_found')
    })

    const userIsSingle = computed(() => userGetters('isSingle'))

    async function onCancelConfirmClick() {
      if (!requestIdToCancel.value) {
        return
      }

      loading.value = true

      try {
        if (userIsBuyer.value) {
          await saleRequestsService.updateManagerSaleRequestStatus(
            requestIdToCancel.value,
            'rejected'
          )
          snackbar.success(t('sale_rejected_successfully'))
        } else {
          await saleRequestsService.cancelSaleRequest(requestIdToCancel.value)
          snackbar.success(t('sale_cancelled_successfully'))
        }
      } catch (err) {
      } finally {
        fetch()

        showCancellationModal.value = false
        requestIdToCancel.value = undefined
        loading.value = false
      }
    }

    async function onBoughtItButtonClick(id: number) {
      loading.value = true

      try {
        await saleRequestsService.updateManagerSaleRequestStatus(
          id,
          'completed'
        )
        snackbar.success(t('sale_completed_successfully'))
      } catch (err) {
        handleError(err as AxiosError, snackbar, logger, t)
      } finally {
        fetch()
        loading.value = false
      }
    }

    function onRejectButtonClick(id: number) {
      showCancellationModal.value = true
      requestIdToCancel.value = id
    }

    function onCancelButtonClick(id: number) {
      showCancellationModal.value = true
      requestIdToCancel.value = id
    }

    async function onNewRequestButtonClick(classifiedId: number) {
      const {
        managers
      } = await saleRequestsService.getManagersWillingToBuyClassified(
        classifiedId
      )

      if (!managers.length) {
        snackbar.error(t('no_managers_willing_to_buy_found'))
        return
      }

      router.push({
        name: '__sale_requests_matched',
        params: { id: classifiedId }
      })
    }

    function handleChangePage(p: number) {
      currentPage.value = p
      scrollService.scrollToTop()
      fetch()
    }

    function getUserName(row: ManagerSaleRequest | GuestSaleRequest) {
      if (userIsBuyer.value) {
        return row.user.name ? row.user.name : row.user.displayName
      } else {
        return row.manager.name ? row.manager.name : row.manager.displayName
      }
    }

    function shouldShowInfoButton(row: ManagerSaleRequest | GuestSaleRequest) {
      return (
        !userIsBuyer.value &&
        row.hasRightToNewRequest &&
        row.status !== 'completed'
      )
    }

    function createBuyerProfileUrl(publicProfile: string) {
      return `${publicProfile}${publicProfile.endsWith('/') ? '' : '/'}contact`
    }

    const { fetch } = useFetch(async () => {
      loading.value = true

      try {
        const { saleRequests } = userIsBuyer.value
          ? await saleRequestsService.getManagerSaleRequests(currentPage.value)
          : await saleRequestsService.getGuestSaleRequests(currentPage.value)

        tableRows.value = saleRequests.rows
        pagination.value = saleRequests.pagination
      } catch (err) {
        handleError(err as AxiosError, snackbar, logger, t)
      } finally {
        loading.value = false
      }
    })

    return {
      getUserName,
      formatNumber,
      handleChangePage,
      onCancelButtonClick,
      format,
      onCancelConfirmClick,
      onBoughtItButtonClick,
      onRejectButtonClick,
      onNewRequestButtonClick,
      shouldShowInfoButton,
      createBuyerProfileUrl,
      loading,
      showBuyConfirmationModal,
      showInfoModal,
      showCancellationModal,
      tableRows,
      pagination,
      requestIdToCancel,
      userIsBuyer,
      actionStatus,
      emptyStateTitle,
      userIsSingle,
      ciSaleRequest,
      ciInfoCircle,
      faExclamationTriangle,
      faTimes,
      SIMPLE_DATE_FORMAT
    }
  },
  i18n: defineComponentTranslations({
    no_sale_requests_found: {
      en: 'No sale requests found',
      el: 'Δεν βρέθηκαν αιτήματα πώλησης'
    },
    no_buy_requests_found: {
      en: 'No buy requests found',
      el: 'Δεν βρέθηκαν αιτήματα αγοράς'
    },
    created_sale_requests_will_appear_here: {
      en: 'Created sale requests will appear here',
      el: 'Τα δημιουργημένα αιτήματα πωλήσεων θα εμφανιστούν εδώ'
    },
    created_buy_requests_will_appear_here: {
      en: 'Created buy requests will appear here',
      el: 'Τα δημιουργημένα αιτήματα αγοράς θα εμφανιστούν εδώ'
    },
    cancel_sale_request: {
      en: 'Cancel request',
      el: 'Ακύρωση αιτήματος'
    },
    yes_cancel_it: {
      en: 'Yes, cancel it',
      el: 'Ναι, ακύρωσε το'
    },
    no_dont_cancel_it: {
      en: "No, don't cancel it",
      el: 'Όχι, μην το ακυρώσεις'
    },
    sale_rejected: {
      en: 'Rejected',
      el: 'Απορρίφθηκε'
    },
    by_cancelling: {
      en: 'By canceling the request, you cannot activate it again.',
      el:
        'Με την ακύρωση του αιτήματος δεν έχεις την δυνατότητα να το ενεργοποιήσεις ξανά.'
    },
    by_cancelling_manager: {
      en: 'By cancelling, you lose the amount you deposited for this request.',
      el: 'Με την ακύρωση χάνεις το ποσό που κατέθεσες για αυτό το αίτημα.'
    },
    sale_cancelled_successfully: {
      en: 'Sale cancelled successfully',
      el: 'Η πώληση ακυρώθηκε επιτυχώς'
    },
    status_info: {
      en:
        'In case a sales request is rejected or expires, you can renew it to a New Request for 2 more days. It is possible to activate the same request up to 2 times.',
      el:
        'Σε περίπτωση που κάποιο αίτημα πώλησης απορριφθεί ή λήξει μπορείς να το ανανεώσεις σε Νέο Αίτημα για 2 ημέρες ακόμα. Υπάρχει η δυνατότητα ενεργοποίησης του ίδιου αιτήματος μέχρι και 2 φορές.'
    },
    classified_seller: {
      en: 'Seller',
      el: 'Πωλητής'
    },
    classified_buyer: {
      en: 'Interested dealer',
      el: 'Ενδιαφερόμενος έμπορος'
    },
    seller_empty_state_description: {
      en:
        'You now have the ability to sell your vehicle to a dealer. Create a new classified and follow the instructions.',
      el:
        'Πλέον έχεις τη δυνατότητα σε κάθε νέα αγγελία οχήματος που καταχωρείς να πωλήσεις το συγκεκριμένο όχημα σε έμπορο. Δημιούργησε μια νέα αγγελία και ακολούθησε τις οδηγίες.'
    },
    sure_to_cancel: {
      en: 'Are you sure you want to cancel the request?',
      el: 'Είσαι σίγουρος ότι θέλεις να ακυρώσεις το αίτημα;'
    },
    no_dont_reject_it: {
      en: "No, don't reject it",
      el: 'Όχι, μην το απορρίψεις'
    },
    yes_reject_it: {
      en: 'Yes, reject it',
      el: 'Ναι, απόρριψε το'
    },
    reject_sale_request: {
      en: 'Reject request',
      el: 'Απόρριψη αιτήματος'
    },
    no_managers_willing_to_buy_found: {
      en: 'No managers willing to buy this classified found. Try again later.',
      el:
        'Δεν βρέθηκαν έμποροι που να ενδιαφέρονται να αγοράσουν την συγκεκριμένη αγγελία αυτή τη στιγμή. Δοκίμασε αργότερα.'
    },
    private_seller_classified: {
      en: 'Private Seller Classified',
      el: 'Αγγελία Ιδιώτη'
    },
    my_classified: {
      en: 'My Classified',
      el: 'Η Αγγελία μου'
    },
    my_offer_price: {
      en: 'My offer price',
      el: 'Τιμή προσφοράς μου'
    },
    manager_offer_price: {
      en: 'Manager offer price',
      el: 'Τιμή προσφοράς έμπορου'
    }
  })
})
